import { Check, X } from "lucide-react";

/**
 * Live order progress line shown on the customer's order page.
 * Delivery:  Placed → Paid → Preparing → Dispatched → Delivered
 * Collection: Placed → Paid → Preparing → Ready for collection → Collected
 */
export function OrderTimeline({ order }) {
  const isDelivery = order.delivery_method === "delivery";
  const steps = isDelivery
    ? ["Order placed", "Payment confirmed", "Being prepared", "Dispatched", "Delivered"]
    : ["Order placed", "Payment confirmed", "Being prepared", "Ready for collection", "Collected"];

  const STAGE = {
    pending_payment: 0,
    processing: 1,
    preparing: 2,
    dispatched: 3,
    ready_for_collection: 3,
    failed_delivery: 3,
    completed: 4,
  };

  if (order.status === "cancelled" || order.status === "refunded") {
    return (
      <div className="mb-7 rounded-2xl border border-red-200 bg-red-50 p-5 flex items-center gap-3" data-testid="order-timeline">
        <span className="w-9 h-9 rounded-full bg-red-100 text-red-500 flex items-center justify-center shrink-0">
          <X size={16} />
        </span>
        <div>
          <p className="text-sm font-bold text-red-600">
            {order.status === "refunded" ? "This order has been refunded" : "This order has been cancelled"}
          </p>
          <p className="text-xs text-red-400 mt-0.5">
            Questions? Contact orders@btmedu.co.za or WhatsApp 061 081 4598.
          </p>
        </div>
      </div>
    );
  }

  const current = STAGE[order.status] ?? 0;
  const outstanding = order.items.filter((i) => (i.outstanding_qty ?? 0) > 0);

  return (
    <div className="mb-7" data-testid="order-timeline">
      <div className="flex items-start">
        {steps.map((label, i) => {
          const done = i <= current;
          const active = i === current;
          return (
            <div key={label} className="flex-1 flex flex-col items-center relative" data-testid={`timeline-step-${i}`}>
              {i > 0 && (
                <span
                  className={`absolute top-4 right-1/2 w-full h-0.5 -z-0 ${i <= current ? "bg-emerald-500" : "bg-slate-200"}`}
                  style={{ transform: "translateY(-50%)" }}
                />
              )}
              <span
                className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                  done
                    ? active
                      ? "bg-gold border-gold text-white gold-glow"
                      : "bg-emerald-500 border-emerald-500 text-white"
                    : "bg-white border-slate-200 text-slate-300"
                }`}
              >
                {i < current ? <Check size={14} /> : i + 1}
              </span>
              <span
                className={`mt-2 text-[9px] sm:text-[10px] font-bold text-center leading-tight ${
                  done ? (active ? "text-brand" : "text-emerald-600") : "text-slate-400"
                }`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
      {order.status === "pending_payment" && (
        <p className="text-[11px] text-slate-400 text-center mt-3">Timeline updates once your payment is confirmed.</p>
      )}
      {order.status === "failed_delivery" && (
        <p className="text-[11px] text-red-500 font-semibold text-center mt-3">
          Delivery attempt failed — we'll contact you to reschedule.
        </p>
      )}
      {outstanding.length > 0 && (
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid="timeline-outstanding">
          <p className="text-xs font-bold text-amber-700">Following separately (free delivery):</p>
          {outstanding.map((i) => (
            <p key={i.product_id} className="text-xs text-amber-700 mt-0.5">
              {i.outstanding_qty} × {i.name}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
